// Catches likely typos in the domain of an email address ("gmial.com", "yaho.com",
// "compass.con") so a form can offer "Did you mean …?". A typo'd address is the
// usual way a lead becomes unreachable. This only ever suggests — it never blocks.

// Providers people actually type. A domain within a typo or two of one of these is
// probably a slip. Includes the common Chinese providers for Lily's clients.
const POPULAR = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com', 'aol.com',
  'live.com', 'msn.com', 'comcast.net', 'sbcglobal.net', 'att.net', 'verizon.net',
  'pacbell.net', 'protonmail.com', 'qq.com', '163.com', '126.com',
];

// Real providers that sit within a typo of a popular one ("mail.com" vs "gmail.com",
// "ymail.com" vs "gmail.com"). Never "correct" these.
const ALSO_REAL = [
  'mail.com', 'email.com', 'ymail.com', 'gmx.com', 'me.com', 'mac.com', 'aim.com',
  'hotmail.co.uk', 'yahoo.co.uk', 'proton.me', 'rocketmail.com',
];

// Mistyped top-level domains, fixed on any domain ("compass.con" -> "compass.com").
const TLD_TYPOS = {
  con: 'com', cmo: 'com', ocm: 'com', vom: 'com', xom: 'com', cpm: 'com', comm: 'com',
  nte: 'net', ner: 'net', nett: 'net', ogr: 'org', orgg: 'org',
};

// Edit distance counting a swap of two neighbouring letters as one edit
// (optimal string alignment), since "gamil" is a single slip, not two.
function distance(a, b) {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

// Returns the corrected address, or null when there's nothing to suggest.
export function suggestEmail(raw) {
  const email = String(raw || '').trim();
  const at = email.lastIndexOf('@');
  if (at < 1) return null;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1).toLowerCase();
  if (!domain.includes('.') || domain.endsWith('.')) return null;
  if (POPULAR.includes(domain) || ALSO_REAL.includes(domain)) return null;

  // Short domains get less slack: two edits would turn "me.com" into almost anything.
  const maxEdits = domain.length <= 7 ? 1 : 2;
  let best = null;
  let bestDistance = Infinity;
  for (const candidate of POPULAR) {
    const dist = distance(domain, candidate);
    if (dist < bestDistance) {
      best = candidate;
      bestDistance = dist;
    }
  }
  if (best && bestDistance <= maxEdits) return `${local}@${best}`;

  const dot = domain.lastIndexOf('.');
  const tld = domain.slice(dot + 1);
  if (TLD_TYPOS[tld]) return `${local}@${domain.slice(0, dot)}.${TLD_TYPOS[tld]}`;

  return null;
}
